
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface PricingPlan {
  id: number;
  name: string;
  price: string;
  originalPrice?: string;
  period: string;
  description: string;
  features: string[];
  isPopular: boolean;
  category: 'web' | 'ecommerce' | 'mobile' | 'marketing';
  badge?: string;
}

export default function PricingSection() {
  const [activeCategory, setActiveCategory] = useState<'web' | 'ecommerce' | 'mobile' | 'marketing'>('web');

  const categories = [
    { id: 'web', name: 'Web Tasarım', icon: 'ri-code-s-slash-line' },
    { id: 'ecommerce', name: 'E-Ticaret', icon: 'ri-shopping-cart-line' },
    { id: 'mobile', name: 'Mobil Uygulama', icon: 'ri-smartphone-line' },
    { id: 'marketing', name: 'Dijital Pazarlama', icon: 'ri-megaphone-line' }
  ];

  const allPlans: PricingPlan[] = [
    // Web Tasarım Paketleri
    {
      id: 1,
      name: 'Temel Web Sitesi',
      price: '4.500',
      period: 'Tek Seferlik',
      description: 'Küçük işletmeler için ideal başlangıç paketi',
      features: [
        '5 Sayfa (Ana Sayfa, Hakkımızda, Hizmetler, İletişim, Blog)',
        'Responsive Tasarım',
        'SSL Sertifikası',
        'Google Analytics',
        '1 Yıl Domain + Hosting',
        '3 Ay Ücretsiz Destek',
        'SEO Temel Optimizasyonu'
      ],
      isPopular: false,
      category: 'web'
    },
    {
      id: 2,
      name: 'Profesyonel Web Sitesi',
      price: '8.500',
      originalPrice: '12.000',
      period: 'Tek Seferlik',
      description: 'Gelişmiş özelliklerle kurumsal web sitesi',
      features: [
        '10 Sayfa + Blog Sistemi',
        'Özel Tasarım',
        'İletişim Formu',
        'Sosyal Medya Entegrasyonu',
        'Canlı Destek Widget',
        '1 Yıl Domain + Hosting',
        '6 Ay Ücretsiz Destek',
        'Gelişmiş SEO',
        'Google Maps Entegrasyonu'
      ],
      isPopular: true,
      category: 'web',
      badge: 'En Popüler'
    },
    {
      id: 3,
      name: 'Premium Web Sitesi',
      price: '15.000',
      period: 'Tek Seferlik',
      description: 'Tam özellikli kurumsal web çözümü',
      features: [
        'Sınırsız Sayfa',
        'Özel Admin Paneli',
        'Çoklu Dil Desteği',
        'Gelişmiş Blog Sistemi',
        'E-Bülten Sistemi',
        'Yedekleme Sistemi',
        '2 Yıl Domain + Hosting',
        '12 Ay Ücretsiz Destek',
        'Premium SEO Paketi',
        '24/7 Teknik Destek'
      ],
      isPopular: false,
      category: 'web'
    },

    // E-Ticaret Paketleri
    {
      id: 4,
      name: 'Başlangıç E-Ticaret',
      price: '12.000',
      period: 'Tek Seferlik',
      description: 'Küçük ölçekli online mağaza',
      features: [
        '50 Ürün Kapasitesi',
        'Temel Ödeme Sistemi',
        'Stok Yönetimi',
        'Sipariş Takibi',
        'Müşteri Paneli',
        '1 Yıl Hosting',
        '3 Ay Destek',
        'Mobil Uyumlu'
      ],
      isPopular: false,
      category: 'ecommerce'
    },
    {
      id: 5,
      name: 'Profesyonel E-Ticaret',
      price: '25.000',
      originalPrice: '35.000',
      period: 'Tek Seferlik',
      description: 'Gelişmiş e-ticaret çözümü',
      features: [
        'Sınırsız Ürün',
        'Gelişmiş Ödeme Seçenekleri',
        'Kargo Entegrasyonu',
        'Kupon & İndirim Sistemi',
        'Raporlama Paneli',
        'SEO Optimizasyonu',
        '2 Yıl Hosting',
        '6 Ay Destek',
        'Sosyal Medya Entegrasyonu'
      ],
      isPopular: true,
      category: 'ecommerce',
      badge: 'Çok Satan'
    },
    {
      id: 6,
      name: 'Enterprise E-Ticaret',
      price: '45.000',
      period: 'Tek Seferlik',
      description: 'Büyük işletmeler için tam çözüm',
      features: [
        'Sınırsız Her Şey',
        'B2B Modülü',
        'Multi-vendor Desteği',
        'Gelişmiş Analytics',
        'API Entegrasyonları',
        'Özel Modül Geliştirme',
        '3 Yıl Hosting',
        '12 Ay Premium Destek',
        'Performans Garantisi'
      ],
      isPopular: false,
      category: 'ecommerce'
    },

    // Mobil Uygulama Paketleri
    {
      id: 7,
      name: 'Temel Mobil Uygulama',
      price: '20.000',
      period: 'Tek Seferlik',
      description: 'Basit ve kullanışlı mobil uygulama',
      features: [
        'iOS + Android',
        '5 Ana Ekran',
        'Push Bildirim',
        'Offline Çalışma',
        'App Store Yayınlama',
        '3 Ay Destek',
        'Temel Analytics'
      ],
      isPopular: false,
      category: 'mobile'
    },
    {
      id: 8,
      name: 'Profesyonel Mobil Uygulama',
      price: '40.000',
      originalPrice: '55.000',
      period: 'Tek Seferlik',
      description: 'Gelişmiş özelliklerle mobil çözüm',
      features: [
        'iOS + Android',
        'Sınırsız Ekran',
        'Kullanıcı Kayıt Sistemi',
        'Ödeme Entegrasyonu',
        'Sosyal Medya Login',
        'Admin Paneli',
        '6 Ay Destek',
        'Gelişmiş Analytics',
        'App Store Optimizasyonu'
      ],
      isPopular: true,
      category: 'mobile',
      badge: 'Önerilen'
    },

    // Dijital Pazarlama Paketleri
    {
      id: 9,
      name: 'SEO Başlangıç',
      price: '2.500',
      period: 'Aylık',
      description: 'Temel SEO optimizasyonu',
      features: [
        'Anahtar Kelime Analizi',
        'Sayfa İçi SEO',
        'Google My Business',
        'Aylık Rapor',
        '10 Anahtar Kelime',
        'Temel Link Building'
      ],
      isPopular: false,
      category: 'marketing'
    },
    {
      id: 10,
      name: 'Tam SEO Paketi',
      price: '5.000',
      originalPrice: '7.000',
      period: 'Aylık',
      description: 'Kapsamlı SEO ve pazarlama',
      features: [
        'Gelişmiş SEO',
        'İçerik Pazarlama',
        'Sosyal Medya Yönetimi',
        'Google Ads Yönetimi',
        'Detaylı Raporlama',
        '25 Anahtar Kelime',
        'Rekabet Analizi',
        'Haftalık Görüşme'
      ],
      isPopular: true,
      category: 'marketing',
      badge: '2024 Özel'
    }
  ];

  const filteredPlans = allPlans.filter(plan => plan.category === activeCategory);

  const scrollToContact = () => {
    const element = document.getElementById('iletisim');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="fiyatlar" className="py-20 bg-gradient-to-br from-gray-800 via-gray-900 to-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/3 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30 mb-6">
            <i className="ri-price-tag-3-line text-blue-400 mr-2"></i>
            <span className="text-blue-400 text-sm font-medium">Fiyatlarımız</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Şeffaf ve Uygun Fiyatlar
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            İhtiyacınıza uygun paketi seçin. Tüm paketlerde kalite garantisi ve 
            müşteri memnuniyeti önceliğimizdir.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id as any)}
              className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-white border border-gray-700/50'
              }`}
            >
              <i className={`${category.icon} mr-2`}></i>
              {category.name}
            </button>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative bg-gradient-to-br backdrop-blur-sm rounded-3xl border p-8 transition-all duration-500 hover:scale-105 hover:shadow-2xl ${
                plan.isPopular
                  ? 'from-blue-500/20 to-cyan-500/20 border-blue-400/50 shadow-2xl shadow-blue-500/20 transform scale-105'
                  : 'from-gray-800/50 to-gray-900/50 border-gray-700/50 hover:border-blue-500/30'
              }`}
            >
              {/* Popular Badge */}
              {plan.isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    {plan.badge}
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.description}</p>
                
                <div className="mb-4">
                  {plan.originalPrice && (
                    <div className="text-gray-500 line-through text-lg mb-1">
                      ₺{plan.originalPrice}
                    </div>
                  )}
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-white">₺{plan.price}</span>
                    <span className="text-gray-400 ml-2">/{plan.period}</span>
                  </div>
                </div>

                {plan.originalPrice && (
                  <div className="inline-flex items-center px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                    <i className="ri-discount-percent-line mr-1"></i>
                    %{Math.round((1 - parseInt(plan.price) / parseInt(plan.originalPrice)) * 100)} İndirim
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start">
                    <i className="ri-check-line text-blue-400 mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button
                onClick={scrollToContact}
                variant={plan.isPopular ? 'primary' : 'outline'}
                className={`w-full ${plan.isPopular ? 'shadow-lg shadow-blue-500/30' : ''}`}
              >
                {plan.category === 'marketing' ? 'Hemen Başla' : 'Teklif Al'}
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>

              {/* Additional Info */}
              <div className="text-center mt-4">
                <p className="text-xs text-gray-500">
                  {plan.category === 'marketing' ? 'İstediğiniz zaman iptal edebilirsiniz' : 'Ücretsiz danışmanlık dahil'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl border border-blue-400/20 p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Özel İhtiyaçlarınız mı Var?
            </h3>
            <p className="text-gray-300 mb-6">
              Standart paketlerimiz dışında özel projeler için de çözüm üretiyoruz. 
              Size özel bir teklif hazırlayalım.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={scrollToContact}>
                Özel Teklif İste
                <i className="ri-mail-line ml-2"></i>
              </Button>
              <a
                href="tel:+903121234567"
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg border border-gray-700 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Hemen Arayın
              </a>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-3xl font-bold text-blue-400 mb-2">100%</div>
            <div className="text-gray-400 text-sm">Müşteri Memnuniyeti</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-cyan-400 mb-2">30 Gün</div>
            <div className="text-gray-400 text-sm">Para İade Garantisi</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-500 mb-2">7/24</div>
            <div className="text-gray-400 text-sm">Teknik Destek</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-400 mb-2">SSL</div>
            <div className="text-gray-400 text-sm">Güvenlik Sertifikası</div>
          </div>
        </div>
      </div>
    </section>
  );
}
